import './ComprobantesPago.css';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { PharmacyTabs, PharmacyMetric, PharmacySelect, PharmacyChartCard, PharmacyLine, PharmacyBar } from './shared/PharmacyUI';
import { Box, Typography, CircularProgress, Tooltip, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, Tooltip as CTooltip, Filler
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, CTooltip, Filler);

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const hoyAnio = () => new Date().getFullYear();
const hoyMes  = () => new Date().getMonth() + 1;
const fmt2    = n => String(n).padStart(2,'0');

// ── Sel custom con portal ─────────────────────────────────────────────────────
const Sel = ({value,onChange,children,w=120}) => <PharmacySelect value={value} onChange={onChange} w={w}>{children}</PharmacySelect>;

// ── useCountUp ────────────────────────────────────────────────────────────────
const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const diff  = target - start;
    if (!diff) return;
    const t0 = performance.now();
    const tick = now => {
      const p    = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(start + diff * ease);
      if (p < 1) requestAnimationFrame(tick);
      else { prev.current = target; setVal(target); }
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return val;
};

// ── KpiCard ───────────────────────────────────────────────────────────────────
const KpiCard = ({label,monto,cantidad,color,icon}) => {
 const animMonto=useCountUp(monto||0,800);
 return <PharmacyMetric label={label} value={'S/ '+animMonto.toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})} color={color} icon={icon} sub={cantidad!==undefined ? cantidad+' comprobante'+(cantidad!==1?'s':'') : 'En el período seleccionado'}/>;
};

// ── PeriodoCard ───────────────────────────────────────────────────────────────
const PeriodoCard = ({ anio, mes, setAnio, setMes }) => (
  <Box className="ann-surface" sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}` }}>
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2,
      letterSpacing: '0.07em', textTransform: 'uppercase', mb: 1 }}>Periodo</Typography>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <input type="number" value={anio} onChange={e => setAnio(Number(e.target.value))}
        min="2020" max="2099"
        style={{ width: 64, height: 34, padding: '0 8px', border: `1px solid ${IOS.separator}`,
          borderRadius: 8, fontSize: '0.8rem', color: IOS.label, backgroundColor: IOS.surface,
          outline: 'none', fontFamily: 'inherit' }}
        onFocus={e => e.target.style.borderColor = IOS.blue}
        onBlur={e  => e.target.style.borderColor = IOS.separator} />
      <Sel value={mes} onChange={e => setMes(Number(e.target.value))} w={90}>
        {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m.slice(0, 3)}</option>)}
      </Sel>
    </Box>
  </Box>
);

// ── TH ────────────────────────────────────────────────────────────────────────
const TH = ({ children, center, right, w }) => (
  <th style={{ padding: '9px 10px', width: w, textAlign: center ? 'center' : right ? 'right' : 'left',
    fontSize: '0.6rem', fontWeight: 700, color: IOS.label2, letterSpacing: '0.06em',
    textTransform: 'uppercase', backgroundColor: IOS.surface,
    borderBottom: `1px solid ${IOS.separator}`, whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1 }}>{children}</th>
);

// ── TimerCircular — recupera countdown desde expiraAt ─────────────────────────
const TimerCircular = ({ expiraAt, token, usado }) => {
  const [segs,    setSegs]    = useState(0);
  const [total,   setTotal]   = useState(0);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!expiraAt || usado) return;
    const fin  = new Date(expiraAt).getTime();
    const calc = () => Math.max(0, Math.round((fin - Date.now()) / 1000));
    const tot  = calc(); setTotal(tot); setSegs(tot);
    const iv = setInterval(() => {
      const s = calc(); setSegs(s); if (s <= 0) clearInterval(iv);
    }, 1000);
    return () => clearInterval(iv);
  }, [expiraAt, usado]);

  const pct      = total > 0 ? segs / total : 0;
  const radio    = 44;
  const circ     = 2 * Math.PI * radio;
  const offset   = circ * (1 - pct);
  const color    = segs > total * 0.5 ? IOS.green : segs > total * 0.2 ? IOS.orange : IOS.red;
  const expirado = segs <= 0 && !usado;

  const copiar = () => {
    navigator.clipboard.writeText(token).then(() => {
      setCopiado(true); setTimeout(() => setCopiado(false), 2000);
    });
  };

  // Si fue USADO — mostrar badge distinto
  if (usado) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
      <Box sx={{ width: 110, height: 110, borderRadius: '50%',
        bgcolor: `${IOS.blue}10`, border: `3px solid ${IOS.blue}30`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round">
          <path d="M20 6L9 17l-5-5"/>
        </svg>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: IOS.blue }}>Usado</Typography>
      </Box>
      <Box sx={{ width: '100%', p: 1.5, bgcolor: `${IOS.blue}06`, borderRadius: '12px',
        border: `1px solid ${IOS.blue}20`, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: IOS.label2,
          textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>Token utilizado</Typography>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: IOS.blue,
          fontFamily: 'monospace', letterSpacing: '0.15em' }}>{token}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
      <Box sx={{ position: 'relative', width: 110, height: 110 }}>
        <svg width="110" height="110" viewBox="0 0 110 110">
          <circle cx="55" cy="55" r={radio} fill="none" stroke={IOS.separator} strokeWidth="8"/>
          <circle cx="55" cy="55" r={radio} fill="none"
            stroke={expirado ? IOS.label3 : color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={offset}
            transform="rotate(-90 55 55)"
            style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.5s' }}/>
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center' }}>
          {expirado
            ? <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: IOS.label2 }}>Expirado</Typography>
            : <>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color, lineHeight: 1, fontFamily: 'monospace' }}>
                  {fmt2(Math.floor(segs / 60))}:{fmt2(segs % 60)}
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: IOS.label2 }}>restante</Typography>
              </>
          }
        </Box>
      </Box>
      <Box sx={{ width: '100%', p: 1.5, bgcolor: `${IOS.blue}06`, borderRadius: '12px',
        border: `1px solid ${IOS.blue}20`, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: IOS.label2,
          textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>Token generado</Typography>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: IOS.blue,
          fontFamily: 'monospace', letterSpacing: '0.15em' }}>{token}</Typography>
      </Box>
      <Box className="pharmacy-action ann-action" component="button" onClick={copiar} disabled={expirado}
        sx={{ width: '100%', height: 36, borderRadius: '10px', border: 'none',
          bgcolor: expirado ? '#E5E5EA' : copiado ? IOS.green : IOS.blue,
          cursor: expirado ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8,
          transition: 'all 0.2s', '&:hover': { filter: expirado ? 'none' : 'brightness(0.92)' } }}>
        {copiado
          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
          : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={expirado ? IOS.label2 : 'white'} strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        }
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: expirado ? IOS.label2 : 'white' }}>
          {expirado ? 'Token expirado' : copiado ? '¡Copiado!' : 'Copiar token'}
        </Typography>
      </Box>
    </Box>
  );
};

const TIEMPOS = [5, 10, 15, 20, 30];

// ── Modal visor PDF preview ───────────────────────────────────────────────────
const ModalPdfVisor = ({ open, onClose, pdfUrl, cargando, total, filtros }) => {
  if (!open) return null;
  return (
    <Box className="ann-pdf-overlay" role="dialog" aria-modal="true" aria-label="Reporte de anulaciones" sx={{ position: 'fixed', inset: 0, zIndex: 9999, bgcolor: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, backdropFilter: 'blur(4px)' }}
      onClick={() => !cargando && onClose()}>
      <Box onClick={e => e.stopPropagation()}
        sx={{ bgcolor: IOS.surface, borderRadius: '20px', overflow: 'hidden',
          width: '92vw', maxWidth: 1100, height: '92vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>

        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: `1px solid ${IOS.separator}`, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: IOS.label }}>
              Reporte de Anulaciones
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.3, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.72rem', color: IOS.label2 }}>
                {total.toLocaleString()} registros
              </Typography>
              {filtros.map((f, i) => (
                <Box key={i} sx={{ display: 'inline-flex', px: 0.8, py: 0.1, borderRadius: '20px',
                  bgcolor: `${IOS.orange}10`, border: `1px solid ${IOS.orange}30` }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: IOS.orange }}>{f}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {pdfUrl && (
              <>
                <Box component="a" href={pdfUrl} download={`Anulaciones_reporte.pdf`}
                  sx={{ height: 32, px: 1.5, borderRadius: '10px', border: `1px solid ${IOS.green}30`,
                    bgcolor: `${IOS.green}08`, cursor: 'pointer', color: IOS.green, fontWeight: 700,
                    fontSize: '0.78rem', textDecoration: 'none', display: 'flex', alignItems: 'center',
                    gap: 0.5, '&:hover': { bgcolor: `${IOS.green}14` } }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/></svg>
                  Descargar
                </Box>
                <Box className="pharmacy-action ann-action" component="button"
                  onClick={() => { const f = document.getElementById('anulaciones-pdf-frame'); if(f) { f.contentWindow.focus(); f.contentWindow.print(); } }}
                  sx={{ height: 32, px: 1.5, borderRadius: '10px', border: `1px solid ${IOS.blue}30`,
                    bgcolor: `${IOS.blue}08`, cursor: 'pointer', display: 'flex', alignItems: 'center',
                    gap: 0.5, '&:hover': { bgcolor: `${IOS.blue}14` } }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: IOS.blue }}>Imprimir</Typography>
                </Box>
              </>
            )}
            <Box className="pharmacy-action ann-action" component="button" onClick={onClose} aria-label="Cerrar reporte"
              sx={{ width: 30, minWidth:30, height: 30, p:0, flexShrink:0, aspectRatio:'1', borderRadius: '50%', border: 'none', bgcolor: '#E5E5EA',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                '&:hover': { bgcolor: '#D1D1D6' } }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </Box>
          </Box>
        </Box>

        {/* Iframe PDF */}
        <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#525659' }}>
          {cargando
            ? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <CircularProgress sx={{ color: 'white' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Generando reporte...</Typography>
              </Box>
            : pdfUrl
              ? <iframe id="anulaciones-pdf-frame" src={pdfUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }} title="Anulaciones PDF" />
              : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>No se pudo generar el reporte</Typography>
                </Box>
          }
        </Box>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const Anulaciones = ({ dependencia = 'CAJA' }) => {
  const navigate   = useNavigate();
  const user       = authService.getCurrentUser();
  const nf         = user?.nombreCompleto || user?.usuario || '';
  const ueId       = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const [tab, setTab] = useState('detalle');

  const [anioD, setAnioD] = useState(hoyAnio());
  const [mesD,  setMesD]  = useState(hoyMes());
  const [anioT, setAnioT] = useState(hoyAnio());
  const [mesT,  setMesT]  = useState(hoyMes());

  // ── Tab Detalle ───────────────────────────────────────────────────────────
  const [kpi,      setKpi]      = useState(null);
  const [diarias,  setDiarias]  = useState([]);
  const [lista,    setLista]    = useState([]);
  const [carg,     setCarg]     = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [refreshD, setRefreshD] = useState(0);
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);

  // PDF export
  const [modalPdf,   setModalPdf]   = useState(false);
  const [pdfUrl,     setPdfUrl]     = useState(null);
  const [cargPdf,    setCargPdf]    = useState(false);

  useEffect(() => {
    if (tab !== 'detalle') return;
    let cancelled = false;
    const fetch = async () => {
      setDiaSeleccionado(null);
      setCarg(true);
      try {
        const p = { params: { periodo: anioD, mes: mesD, idUe: ueId, dependencia } };
        const [rKpi, rDia, rLis] = await Promise.all([
          api.get('/caja/anulaciones/kpi',     p),
          api.get('/caja/anulaciones/diarias', p),
          api.get('/caja/anulaciones/listar',  p),
        ]);
        if (cancelled) return;
        if (rKpi.data.success) setKpi(rKpi.data.data);
        if (rDia.data.success) setDiarias(rDia.data.data || []);
        if (rLis.data.success) setLista(rLis.data.data || []);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setCarg(false); }
    };
    fetch();
    return () => { cancelled = true; };
  }, [tab, anioD, mesD, ueId, refreshD]);

  const listaFilt = useMemo(() => {
  let f = lista;
  if(diaSeleccionado){
    f = f.filter(r => {
      const dd = String(r.fechaAnulacion || '').split('/')[0].padStart(2,'0');
      return dd === String(diaSeleccionado).padStart(2,'0');
    });
  }
  if(busqueda){
    const b = busqueda.toLowerCase();
    f = f.filter(r =>
      (r.comprobante||'').toLowerCase().includes(b) ||
      (r.cajero||'').toLowerCase().includes(b) ||
      (r.motivo||'').toLowerCase().includes(b)
    );
  }
  return f;
},[lista, busqueda, diaSeleccionado]);

  // Filtros activos para el Excel
  const filtrosActivos = useMemo(() => {
    const f = [];
    if (busqueda) f.push(`Búsqueda: "${busqueda}"`);
    return f;
  }, [busqueda]);

  const verPdf = async () => {
    if (cargPdf) return;
    setPdfUrl(null);
    setCargPdf(true);
    setModalPdf(true);
    try {
      const resp = await api.post('/caja/anulaciones/exportar-pdf', {
        periodo:    anioD,
        mes:        mesD,
        busqueda:   busqueda || null,
        filtroTipo: null,
        datos:      listaFilt,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch (e) { console.error(e); setModalPdf(false); }
    finally { setCargPdf(false); }
  };

  const closePdf = () => {
    setModalPdf(false);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  };

  // ── Gráfico — key fuerza remount para animación al cambiar mes ─────────────
  const chartKey = `chart-${anioD}-${mesD}`;
  const [tipoGrafico, setTipoGrafico] = useState('area'); // 'area' | 'line' | 'bar'
  const esBarrasAnul = tipoGrafico === 'bar';

  const chartDiario = {
    labels: diarias.map(d => d.dia),
    datasets: [{
      label: 'Monto anulado',
      data: diarias.map(d => d.monto || 0),
      borderColor: esBarrasAnul ? 'transparent' : '#CF7881',
      borderWidth: esBarrasAnul ? 0 : 2.5,
      borderRadius: esBarrasAnul ? 6 : 0,
      backgroundColor: ctx => {
        if (esBarrasAnul) return '#CF7881';
        if (!ctx.chart.chartArea) return 'rgba(207,120,129,0.08)';
        const { top, bottom } = ctx.chart.chartArea;
        const g = ctx.chart.ctx.createLinearGradient(0, top, 0, bottom);
        g.addColorStop(0, 'rgba(207,120,129,0.22)'); g.addColorStop(1, 'rgba(207,120,129,0.01)');
        return g;
      },
      fill: tipoGrafico === 'area', tension: 0.45,
      pointRadius: 0, pointHoverRadius: esBarrasAnul ? 0 : 5,
      pointBackgroundColor: '#CF7881', pointBorderColor: 'white', pointBorderWidth: 2,
      pointHitRadius: 20,  // área de detección mucho más grande — fix tooltip
    }]
  };
  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeOutQuart',
    },
    onClick: (evt, elements, chart) => {
      if(!elements.length) return;
      const dia = chart.data.labels[elements[0].index];
      setDiaSeleccionado(prev => { const n = prev===dia?null:dia; return n; });
    },
    animations: {
      // Cada punto aparece con delay escalonado de izquierda a derecha
      radius: {
        duration: 400,
        easing: 'easeOutCubic',
        from: 0,
        to: 3,
        loop: false,
      },
    },
    transitions: {
      active: { animation: { duration: 150 } },
    },
    interaction: { mode: 'index', axis: 'x', intersect: false },
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(28,28,30,0.92)', titleColor: '#fff',
        bodyColor: 'rgba(255,255,255,0.75)', padding: 12, borderRadius: 12, displayColors: false,
        callbacks: {
          title: items => `Día ${items[0].label}`,
          label: ctx => ` S/ ${ctx.parsed.y.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`,
        }
      }
    },
    scales: {
      x: { grid: { display: false }, border: { display: false },
           ticks: { font: { size: 10 }, color: IOS.label2 } },
      y: { min: 0, border: { display: false },
           grid: { color: 'rgba(60,60,67,0.05)' },
           ticks: { callback: v => 'S/ '+Number(v).toLocaleString('es-PE',{maximumFractionDigits:0}),
                    font: { size: 10 }, color: IOS.label2, maxTicksLimit: 5 } },
    },
  };

  // ── Tab Tokens ────────────────────────────────────────────────────────────
  const [cajeros,     setCajeros]     = useState([]);
  const [cajeroSel,   setCajeroSel]   = useState('');
  const [minutos,     setMinutos]     = useState(10);
  const [tokens,      setTokens]      = useState([]);
  const [topAnul,     setTopAnul]     = useState([]);
  const [cargTok,     setCargTok]     = useState(false);
  const [generando,   setGenerando]   = useState(false);
  const [tokenActivo, setTokenActivo] = useState(null);
  const [refreshT,    setRefreshT]    = useState(0);

  const [leftColHeight, setLeftColHeight] = useState(null);
  const leftColRoRef = useRef(null); // guarda la instancia del ResizeObserver activo
  const leftColRef = useCallback((node) => {
    if (leftColRoRef.current) { leftColRoRef.current.disconnect(); leftColRoRef.current = null; }
    if (node) {
      const ro = new ResizeObserver(entries => {
        const isLgUp = window.innerWidth >= 1200; // breakpoint 'lg' de MUI
        setLeftColHeight(isLgUp ? entries[0].contentRect.height : null);
      });
      ro.observe(node);
      leftColRoRef.current = ro;
    }
  }, []);

  useEffect(() => {
    if (tab !== 'tokens') return;
    let cancelled = false;
    const fetch = async () => {
      setCargTok(true);
      try {
        const p = { params: { periodo: anioT, mes: mesT, idUe: ueId, dependencia } };
        const [rTok, rTop, rCaj] = await Promise.all([
          api.get('/caja/anulaciones/tokens',          p),
          api.get('/caja/anulaciones/top-anuladores',  p),
          api.get('/caja/anulaciones/cajeros',         { params: { idUe: ueId } }),
        ]);
        if (cancelled) return;
        if (rTok.data.success) {
          setTokens(rTok.data.data || []);
          // Recuperar token activo tras refresh — busca el más reciente vigente
          const vigente = (rTok.data.data || []).find(t =>
            (t.estado === 'ACTIVO' || t.estado === 'VIGENTE') && t.expiraAt
          );
          // Solo restaurar si no hay uno activo ya en pantalla
          if (vigente && !tokenActivo) {
            setTokenActivo({ token: vigente.tokenCode, expiraAt: vigente.expiraAt });
          }
        }
        if (rTop.data.success) setTopAnul(rTop.data.data || []);
        if (rCaj.data.success) setCajeros(rCaj.data.data || []);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setCargTok(false); }
    };
    fetch();
    return () => { cancelled = true; };
  }, [tab, anioT, mesT, ueId, refreshT]);

  const cargarTokens = () => setRefreshT(r => r + 1);

  const generarToken = async () => {
    if (!cajeroSel || generando) return;
    setGenerando(true);
    try {
      const r = await api.post('/caja/anulaciones/generar-token', { autorizaA: cajeroSel, minutos, dependencia });
      if (r.data.success) {
        setTokenActivo({ token: r.data.token, expiraAt: r.data.expiraAt, usado: false });
        cargarTokens();
      }
    } catch (e) { console.error(e); } finally { setGenerando(false); }
  };

  const copiarToken = tok => navigator.clipboard.writeText(tok).catch(console.error);

  // Determinar si el token activo fue usado
  const tokenActivoUsado = useMemo(() => {
    if (!tokenActivo) return false;
    const tok = tokens.find(t => t.tokenCode === tokenActivo.token);
    return tok?.estado === 'USADO';
  }, [tokenActivo, tokens]);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      <Box className="ann-page pharmacy-page cpe-page" sx={{ minHeight: '100vh', bgcolor: IOS.bg }}>
        <HeaderIOS titulo="Anulaciones" subtitulo={dependencia === 'DATAFARMA' ? 'DataFarma' : 'Caja'}
          onHome={() => navigate(dependencia === 'DATAFARMA' ? '/datafarma' : '/caja')} onLogout={handleLogout}
          nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()} />

        <Box className="pharmacy-content cpe-content" sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 1.5, sm: 2.6 }, pt: 2.5, pb: 6 }}>

          <Box className="pharmacy-inline-tabs"><PharmacyTabs value={tab} onChange={setTab} options={[{value:'detalle',label:'Anulaciones'},{value:'tokens',label:'Autorizaciones'}]}/></Box>
          <Box className="cpe-intro"><Typography className="cpe-eyebrow">Anulaciones · {dependencia==='DATAFARMA'?'Datafarma':'Caja'}</Typography><Typography component="h1">{tab==='detalle'?'Cada anulación, en contexto':'Autoriza con control'}</Typography><Typography>{tab==='detalle'?'Revisa los comprobantes anulados, sus motivos y quién autorizó cada operación.':'Genera autorizaciones temporales y consulta su uso por cajero.'}</Typography></Box>

          {/* ══ TAB DETALLE ══ */}
          {tab === 'detalle' && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0,1fr))', lg: 'minmax(170px,.8fr) repeat(4,minmax(0,1fr))' },
                gap: 1.5, mb: 2 }}>
                <PeriodoCard anio={anioD} mes={mesD} setAnio={setAnioD} setMes={setMesD} />
                <KpiCard label="Total anulados" monto={kpi?.totalMontoAnulado || 0}
                  cantidad={kpi?.totalAnulaciones} color={IOS.red}
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}/>
                <KpiCard label="Boletas" monto={kpi?.totalBoletas || 0}
                  cantidad={kpi?.cantBoletas} color={IOS.blue}
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}/>
                <KpiCard label="Facturas" monto={kpi?.totalFacturas || 0}
                  cantidad={kpi?.cantFacturas} color={IOS.orange}
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>}/>
                <KpiCard label="Tickets" monto={kpi?.totalTickets || 0}
                  cantidad={kpi?.cantTickets} color={IOS.purple}
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="1.8" strokeLinecap="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z"/></svg>}/>
              </Box>

              <Box sx={{mb:2.5}}><PharmacyChartCard title="Evolución de anulaciones" sub={MESES[mesD-1]+' '+anioD+' · Selecciona un día para ver sus comprobantes'} right={<Box className="ann-chart-types"><PharmacyTabs value={tipoGrafico} onChange={setTipoGrafico} label="Tipo de gráfico" options={[{value:'area',label:'Área'},{value:'line',label:'Línea'},{value:'bar',label:'Barras'}]}/></Box>}>
                <Box className="ann-chart-summary"><span>Monto anulado en el período</span><strong>S/ {diarias.reduce((sum,d)=>sum+Number(d.monto||0),0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></Box>
                <Box key={chartKey+'-'+tipoGrafico} sx={{height:{xs:220,sm:270}}}>
                  {carg ? <Box className="ann-empty"><CircularProgress size={24}/></Box> : diarias.length===0 ? <Box className="ann-empty">Sin actividad en este período</Box> : tipoGrafico==='bar' ? <PharmacyBar data={chartDiario} options={chartOpts}/> : <PharmacyLine data={chartDiario} options={chartOpts}/>}
                </Box>
                {diaSeleccionado&&<Box className="ann-chart-filter"><span>Mostrando el día <strong>{diaSeleccionado}</strong></span><button type="button" onClick={()=>setDiaSeleccionado(null)}>Ver todo el período</button></Box>}
              </PharmacyChartCard></Box>

              {/* Tabla */}
              <Box className="ann-surface" sx={{ bgcolor: IOS.surface, borderRadius: '16px',
                border: `1px solid ${IOS.separator}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${IOS.separator}`,
                  display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ position: 'relative', flex: 1, minWidth: 180 }}>
                    <Box sx={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: IOS.label2, pointerEvents: 'none' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </Box>
                    <input placeholder="Buscar comprobante, cajero, motivo..."
                      value={busqueda} onChange={e => setBusqueda(e.target.value)}
                      style={{ width: '100%', height: 32, paddingLeft: 26, paddingRight: 8,
                        border: `1px solid ${IOS.separator}`, borderRadius: 8, fontSize: '0.76rem',
                        color: IOS.label, backgroundColor: IOS.bg, outline: 'none',
                        fontFamily: 'inherit', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = IOS.red}
                      onBlur={e  => e.target.style.borderColor = IOS.separator} />
                  </Box>

                  {/* Botón refresh */}
                  <Box className="pharmacy-action ann-action" component="button" aria-label="Actualizar anulaciones" onClick={() => setRefreshD(r => r + 1)}
                    sx={{ width: 30, height: 30, borderRadius: '50%', border: `1px solid ${IOS.separator}`,
                      bgcolor: IOS.bg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { bgcolor: `${IOS.red}10` } }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2.2" strokeLinecap="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                  </Box>

                  {/* Botón PDF */}
                  <Tooltip title="Ver PDF" placement="top">
                    <Box className="pharmacy-action ann-action" component="button" aria-label="Descargar reporte PDF" onClick={() => listaFilt.length > 0 && verPdf()}
                      disabled={listaFilt.length === 0}
                      sx={{ width: 30, height: 30, borderRadius: '50%', border: 'none',
                        bgcolor: listaFilt.length === 0 ? IOS.separator : IOS.green,
                        cursor: listaFilt.length === 0 ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: listaFilt.length === 0 ? 0.4 : 1,
                        transition: 'all 0.15s', '&:hover': { filter: listaFilt.length === 0 ? 'none' : 'brightness(0.9)' } }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
                        <path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/>
                      </svg>
                    </Box>
                  </Tooltip>

                  <Box sx={{ px: 1, py: 0.3, bgcolor: IOS.bg, borderRadius: '20px', border: `1px solid ${IOS.separator}` }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: IOS.red }}>
                      {listaFilt.length} registros
                    </Typography>
                  </Box>
                </Box>

                {carg
                  ? <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: IOS.red }} /></Box>
                  : <>
                      {/* ── MÓVIL: cards ── */}
                      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        {listaFilt.length === 0
                          ? <Box sx={{ py: 6, textAlign: 'center' }}>
                              <Typography sx={{ color: IOS.label2, fontSize: '0.82rem' }}>Sin anulaciones este período</Typography>
                            </Box>
                          : listaFilt.map((r, i) => {
                              const colorTipo = r.tipo?.includes('BOL') ? IOS.blue : r.tipo?.includes('FAC') ? IOS.orange : IOS.purple;
                              return (
                                <Box key={i} sx={{ p: 2, borderBottom: `1px solid ${IOS.separator}` }}>
                                  {/* Fila 1: tipo + comprobante + monto */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
                                        <Box sx={{ px: 0.7, py: 0.2, borderRadius: '6px', bgcolor: `${colorTipo}10`, flexShrink: 0 }}>
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: colorTipo }}>{r.tipo}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: IOS.label,
                                          fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {r.comprobante}
                                        </Typography>
                                      </Box>
                                      <Typography sx={{ fontSize: '0.68rem', color: IOS.label2 }}>{r.cajero}</Typography>
                                    </Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: IOS.red, flexShrink: 0 }}>
                                      S/ {(r.monto || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                                    </Typography>
                                  </Box>

                                  {/* Fila 2: chips info */}
                                  <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 1 }}>
                                    <Box sx={{ px: 0.8, py: 0.2, borderRadius: '20px', bgcolor: IOS.bg, border: `1px solid ${IOS.separator}` }}>
                                      <Typography sx={{ fontSize: '0.6rem', color: IOS.label2 }}>{r.fechaAnulacion} {r.horaAnulacion}</Typography>
                                    </Box>
                                    {r.token && (
                                      <Box sx={{ px: 0.8, py: 0.2, borderRadius: '20px', bgcolor: `${IOS.orange}10`, border: `1px solid ${IOS.orange}20` }}>
                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.orange, fontFamily: 'monospace' }}>{r.token}</Typography>
                                      </Box>
                                    )}
                                    {r.tokenEstado && (
                                      <Box sx={{ px: 0.8, py: 0.2, borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                        bgcolor: r.tokenEstado === 'USADO' ? `${IOS.green}10` : `${IOS.orange}10`,
                                        border: `1px solid ${r.tokenEstado === 'USADO' ? IOS.green : IOS.orange}25` }}>
                                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: r.tokenEstado === 'USADO' ? IOS.green : IOS.orange }} />
                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700,
                                          color: r.tokenEstado === 'USADO' ? IOS.green : IOS.orange }}>{r.tokenEstado}</Typography>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Fila 3: motivo si hay */}
                                  {r.motivo && (
                                    <Box sx={{ px: 1, py: 0.5, bgcolor: IOS.bg, borderRadius: '8px', border: `1px solid ${IOS.separator}` }}>
                                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2,
                                        textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.2 }}>Motivo</Typography>
                                      <Typography sx={{ fontSize: '0.72rem', color: IOS.label }}>{r.motivo}</Typography>
                                    </Box>
                                  )}
                                  {/* Nota afectada */}
                                  {r.comprobanteAfect && (
                                    <Typography sx={{ fontSize: '0.62rem', color: IOS.label2, mt: 0.5, fontFamily: 'monospace' }}>
                                      Nota: {r.comprobanteAfect}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })
                        }
                      </Box>

                      {/* ── DESKTOP: tabla ── */}
                      <Box sx={{ display: { xs: 'none', md: 'block' },
                        overflowX: 'auto', overflowY: 'auto', maxHeight: 440,
                        '&::-webkit-scrollbar': { width: 4, height: 4 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: IOS.separator, borderRadius: 4 } }}>
                        <table className="ann-records-table ann-main-table" style={{minWidth:960}}>
                          <thead><tr><TH>Comprobante</TH><TH>Anulación</TH><TH>Cajero</TH><TH>Autorización</TH><TH>Motivo</TH><TH right>Monto</TH></tr></thead>
                          <tbody>{listaFilt.length===0 ? <tr><td colSpan={6}><div className="ann-empty">Sin anulaciones para estos filtros</div></td></tr> : listaFilt.map((r,i)=><tr key={i} className="ann-record-main">
                              <td><span className={'ann-document-type '+(r.tipo?.includes('BOL')?'receipt':r.tipo?.includes('FAC')?'invoice':'other')}>{r.tipo||'Comprobante'}</span><strong className="ann-document-number">{r.comprobante}</strong>{r.comprobanteAfect&&<span className="ann-cell-sub">Nota · {r.comprobanteAfect}</span>}</td>
                              <td><strong>{r.fechaAnulacion}</strong><span className="ann-cell-sub">{r.horaAnulacion}</span></td>
                              <td><strong>{r.cajero||'—'}</strong></td>
                              <td>{r.token ? <><strong>{r.autorizadoPor||'Sin nombre registrado'}</strong><span className="ann-cell-sub ann-token-reference">Token · {r.token}</span>{r.tokenEstado&&<span className={'ann-status '+(r.tokenEstado==='USADO'?'used':'active')}>{r.tokenEstado}</span>}</> : <><span className="ann-status elevated">Privilegio elevado</span><span className="ann-cell-sub">Sin token</span></>}</td>
                              <td className="ann-reason-cell"><span>{r.motivo||'Sin motivo registrado'}</span></td>
                              <td className="ann-amount"><span>S/ {Number(r.monto||0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}</span></td>
                          </tr>)}</tbody>
                        </table>
                      </Box>
                    </>
                }
              </Box>
            </Box>
          )}

          {/* ══ TAB TOKENS ══ */}
          {tab === 'tokens' && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={4} ref={leftColRef}>
                {/* Periodo */}
                <Box className="ann-surface" sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2, mb: 2,
                  border: `1px solid ${IOS.separator}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: IOS.label2,
                    textTransform: 'uppercase', letterSpacing: '0.07em', mb: 1 }}>Periodo</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <input type="number" value={anioT} onChange={e => setAnioT(Number(e.target.value))}
                      min="2020" max="2099"
                      style={{ width: 64, height: 34, padding: '0 8px', border: `1px solid ${IOS.separator}`,
                        borderRadius: 8, fontSize: '0.8rem', color: IOS.label, backgroundColor: IOS.surface,
                        outline: 'none', fontFamily: 'inherit' }}
                      onFocus={e => e.target.style.borderColor = IOS.blue}
                      onBlur={e  => e.target.style.borderColor = IOS.separator} />
                    <Sel value={mesT} onChange={e => setMesT(Number(e.target.value))} w={100}>
                      {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m.slice(0, 3)}</option>)}
                    </Sel>
                  </Box>
                </Box>

                {/* Generar token */}
                <Box className="ann-surface" sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2.5, mb: 2,
                  border: `1px solid ${IOS.separator}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: `${IOS.orange}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: IOS.label }}>Generar token</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2,
                        textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.6 }}>Autorizar cajero</Typography>
                      <Sel value={cajeroSel} onChange={e => setCajeroSel(e.target.value)} w="100%">
                        {[{ usuario: '', cajero: 'Seleccionar cajero...' }, ...cajeros].map(c =>
                          <option key={c.usuario} value={c.usuario}>{c.cajero}</option>
                        )}
                      </Sel>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2,
                        textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.6 }}>Tiempo de validez</Typography>
                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                        {TIEMPOS.map(t => (
                          <Box key={t} onClick={() => setMinutos(t)}
                            sx={{ px: 1.2, py: 0.5, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s',
                              border: `1.5px solid ${minutos === t ? IOS.orange : IOS.separator}`,
                              bgcolor: minutos === t ? `${IOS.orange}08` : IOS.bg }}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: minutos === t ? 700 : 500,
                              color: minutos === t ? IOS.orange : IOS.label2 }}>{t} min</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box className="pharmacy-action ann-action" component="button" onClick={generarToken} disabled={!cajeroSel || generando}
                      sx={{ height: 38, borderRadius: '10px', border: 'none',
                        bgcolor: !cajeroSel || generando ? '#E5E5EA' : IOS.orange,
                        cursor: !cajeroSel || generando ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8,
                        transition: 'all 0.2s', '&:hover': { filter: cajeroSel && !generando ? 'brightness(0.92)' : 'none' } }}>
                      {generando && <Box sx={{ width: 14, height: 14, borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white',
                        animation: 'spin 0.8s linear infinite',
                        '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }}/>}
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700,
                        color: !cajeroSel || generando ? '#8E8E93' : 'white' }}>
                        {generando ? 'Generando...' : 'Generar token'}
                      </Typography>
                    </Box>
                  </Box>
                  {tokenActivo && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${IOS.separator}` }}>
                      <TimerCircular
                        token={tokenActivo.token}
                        expiraAt={tokenActivo.expiraAt}
                        usado={tokenActivoUsado}
                      />
                    </Box>
                  )}
                </Box>

                {/* Top anuladores */}
                <Box className="ann-surface" sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2,
                  border: `1px solid ${IOS.separator}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: IOS.label, mb: 1.5 }}>
                    Top anuladores
                  </Typography>
                  {cargTok
                    ? <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={18} sx={{ color: IOS.red }} /></Box>
                    : topAnul.length === 0
                      ? <Typography sx={{ fontSize: '0.76rem', color: IOS.label2, textAlign: 'center', py: 2 }}>Sin datos</Typography>
                      : topAnul.map((t, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2,
                            pb: 1.2, borderBottom: i < topAnul.length - 1 ? `1px solid ${IOS.separator}` : 'none' }}>
                            <Box sx={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                              bgcolor: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : `${IOS.red}10`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700,
                                color: i < 3 ? 'white' : IOS.red }}>{i + 1}</Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: IOS.label,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {t.usuario}
                              </Typography>
                              <Typography sx={{ fontSize: '0.62rem', color: IOS.label2 }}>{t.cajNombre}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: IOS.red }}>
                                {t.cantidad || 0}
                              </Typography>
                              <Typography sx={{ fontSize: '0.58rem', color: IOS.label2 }}>
                                {(t.porcentaje || 0).toFixed(1)}%
                              </Typography>
                            </Box>
                          </Box>
                        ))
                  }
                </Box>
              </Grid>

              {/* Col derecha — tabla tokens */}
              <Grid item xs={12} lg={8}>
                <Box className="ann-surface" sx={{ bgcolor: IOS.surface, borderRadius: '16px', border: `1px solid ${IOS.separator}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden',
                  height: leftColHeight ? `${leftColHeight}px` : 'auto',
                  display:'flex', flexDirection:'column' }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${IOS.separator}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink:0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: IOS.label }}>
                      Historial de autorizaciones
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ px: 1, py: 0.3, bgcolor: IOS.bg, borderRadius: '20px', border: `1px solid ${IOS.separator}` }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: IOS.orange }}>
                          {tokens.length} tokens
                        </Typography>
                      </Box>
                      <Box className="pharmacy-action ann-action" component="button" aria-label="Actualizar autorizaciones" onClick={cargarTokens}
                        sx={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${IOS.separator}`,
                          bgcolor: IOS.bg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          '&:hover': { bgcolor: `${IOS.orange}10` } }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2.2" strokeLinecap="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                      </Box>
                    </Box>
                  </Box>
                  {cargTok
                    ? <Box sx={{ py: 8, textAlign: 'center', flex:1 }}><CircularProgress sx={{ color: IOS.orange }} /></Box>
                    : <Box sx={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
                        {/* ── MÓVIL: cards tokens ── */}
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                          {tokens.length === 0
                            ? <Box sx={{ py: 6, textAlign: 'center' }}>
                                <Typography sx={{ color: IOS.label2, fontSize: '0.82rem' }}>Sin tokens generados este período</Typography>
                              </Box>
                            : tokens.map((t, i) => {
                                const activo = t.estado === 'ACTIVO' || t.estado === 'VIGENTE';
                                const usado  = t.estado === 'USADO';
                                const colorEst = activo ? IOS.green : usado ? IOS.blue : IOS.label3;
                                return (
                                  <Box key={i} sx={{ p: 2, borderBottom: `1px solid ${IOS.separator}` }}>
                                    {/* Token + estado */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 800,
                                        fontSize: '1rem', color: IOS.orange, letterSpacing: '0.12em' }}>
                                        {t.tokenCode}
                                      </Typography>
                                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                        px: 0.8, py: 0.3, borderRadius: '20px',
                                        bgcolor: activo ? `${IOS.green}10` : usado ? `${IOS.blue}10` : `${IOS.label3}10`,
                                        border: `1px solid ${colorEst}20` }}>
                                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: colorEst }} />
                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700,
                                          color: activo ? '#1A6B3C' : usado ? IOS.blue : IOS.label2 }}>
                                          {t.estado}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {/* Generado por → Autoriza a */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                      <Typography sx={{ fontSize: '0.72rem', color: IOS.label2 }}>{t.generadoPor}</Typography>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.label3} strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: IOS.label }}>{t.autorizaA}</Typography>
                                    </Box>
                                    {/* Fechas + acción */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Box>
                                        <Typography sx={{ fontSize: '0.6rem', color: IOS.label2 }}>Generado: {t.fechaGeneracion}</Typography>
                                        {t.fechaUso && <Typography sx={{ fontSize: '0.6rem', color: IOS.label2 }}>Usado: {t.fechaUso}</Typography>}
                                      </Box>
                                      {activo
                                        ? <Box component="button" className="pharmacy-action ann-action" aria-label="Copiar token" onClick={() => copiarToken(t.tokenCode)}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.6,
                                              px: 1.2, py: 0.5, borderRadius: '8px',
                                              bgcolor: `${IOS.orange}12`, border: `1px solid ${IOS.orange}25`,
                                              cursor: 'pointer', '&:hover': { bgcolor: `${IOS.orange}22` } }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: IOS.orange }}>Copiar</Typography>
                                          </Box>
                                        : <Box sx={{ px: 1, py: 0.4, borderRadius: '8px', bgcolor: IOS.separator }}>
                                            <Typography sx={{ fontSize: '0.62rem', color: IOS.label2 }}>
                                              {usado ? 'Usado' : 'Expirado'}
                                            </Typography>
                                          </Box>
                                      }
                                    </Box>
                                  </Box>
                                );
                              })
                          }
                        </Box>

                        {/* ── DESKTOP: tabla tokens ── */}
                        <Box sx={{ display: { xs: 'none', md: 'block' }, flex:1, minHeight:0,
                          overflowX: 'auto', overflowY: 'auto',
                          '&::-webkit-scrollbar': { width: 3, height: 3 },
                          '&::-webkit-scrollbar-thumb': { bgcolor: IOS.separator, borderRadius: 4 } }}>
                          <table className="ann-records-table" style={{minWidth:620}}><thead><tr><TH>Autorización</TH><TH>Responsables</TH><TH>Actividad</TH><TH right>Acción</TH></tr></thead><tbody>
                          {tokens.length===0 ? <tr><td colSpan={4}><div className="ann-empty">Sin autorizaciones en este período</div></td></tr> : tokens.map((t,i)=>{
                            const activo=t.estado==='ACTIVO'||t.estado==='VIGENTE';
                            return <tr key={i}><td><strong className="ann-token-code">{t.tokenCode}</strong><span className={'ann-status '+(activo?'active':t.estado==='USADO'?'used':'expired')}>{t.estado}</span></td>
                            <td><strong>{t.autorizaA||'—'}</strong><span className="ann-cell-sub">Autorizado por {t.generadoPor||'—'}</span></td>
                            <td><span className="ann-cell-sub">Generado</span><strong>{t.fechaGeneracion}</strong>{t.fechaUso&&<><span className="ann-cell-sub">Utilizado</span><strong>{t.fechaUso}</strong></>}</td>
                            <td style={{textAlign:'right'}}><button className="ann-copy-button" type="button" disabled={!activo} onClick={()=>copiarToken(t.tokenCode)} aria-label={'Copiar token '+t.tokenCode}>{activo?'Copiar':t.estado==='USADO'?'Utilizado':'Expirado'}</button></td></tr>;
                          })}</tbody></table>
                        </Box>
                      </Box>
                  }
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>

      {/* Modal PDF visor */}
      <ModalPdfVisor
        open={modalPdf}
        onClose={closePdf}
        pdfUrl={pdfUrl}
        cargando={cargPdf}
        total={listaFilt.length}
        filtros={filtrosActivos}
      />
      <style>{`
        
.ann-page .pharmacy-tabs{background:#eaeff5;border:1px solid #e0e5ed;border-radius:14px;padding:4px;width:fit-content;max-width:100%}
.ann-page .ann-surface{border-radius:20px;border-color:#e2e7ef;box-shadow:0 3px 16px rgba(25,45,75,.035)}
.ann-page .pharmacy-metric{height:100%;box-sizing:border-box}
.ann-page .pharmacy-select{border-radius:12px}
.ann-page input{box-sizing:border-box}
.ann-page input:focus-visible,.ann-page button:focus-visible{outline:2px solid #528de1;outline-offset:3px}
.ann-page table{border-collapse:collapse;width:100%}
.ann-page table th{background:#f5f7fa!important;text-transform:none!important;letter-spacing:0!important;font-size:11px!important;padding:14px 12px!important;color:#73829a!important}
.ann-page table td{padding:12px!important;vertical-align:top}
.ann-page table tbody tr{border-bottom:1px solid #edf0f5;transition:background .15s ease}
.ann-page table tbody tr:hover{background:#f7f9fc}
.ann-pdf-overlay{background:rgba(24,39,61,.36)!important;backdrop-filter:blur(3px)!important}
.ann-pdf-overlay>div{border-radius:24px!important;box-shadow:0 24px 80px #25344930!important;border:1px solid #e2e7ef}
@media(max-width:600px){.ann-page .pharmacy-tabs{width:100%}.ann-page .pharmacy-metric{padding:16px}.ann-page .pharmacy-metric-value{font-size:22px}.ann-pdf-overlay{padding:10px!important}.ann-pdf-overlay>div{width:100%!important;border-radius:18px!important}}


.ann-page .ann-action,.ann-pdf-overlay .ann-action{min-height:40px!important;box-sizing:border-box;border-radius:10px!important;flex-shrink:0;box-shadow:none!important}
.ann-page .ann-action[aria-label="Actualizar anulaciones"],.ann-page .ann-action[aria-label="Actualizar autorizaciones"],.ann-page .ann-action[aria-label="Descargar reporte PDF"]{width:40px!important;min-width:40px!important;height:40px!important;padding:0!important}
.ann-pdf-overlay a[download]{min-height:40px;border-radius:10px!important;box-sizing:border-box}

.ann-pdf-overlay .ann-action[aria-label="Cerrar reporte"]{width:32px!important;min-width:32px!important;height:32px!important;min-height:32px!important;border-radius:50%!important}
.ann-page .ann-surface{border-radius:20px!important;border-color:#e2e7ef!important;box-shadow:0 3px 12px #26365305!important}
.ann-chart-types .pharmacy-tabs{min-height:36px;padding:3px}
.ann-chart-types .MuiTab-root{min-height:30px;min-width:55px;padding:5px 12px;font-size:11px}
.ann-chart-summary{display:flex;flex-direction:column;gap:6px;margin-bottom:18px;color:#7a8799;font-size:12px}
.ann-chart-summary strong{font-size:26px;color:#29374b;font-weight:650;letter-spacing:-.5px}
.ann-chart-filter{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-top:16px;padding-top:16px;border-top:1px solid #edf0f5;color:#667085;font-size:12px}
.ann-chart-filter button,.ann-copy-button{min-height:36px;padding:8px 12px;border:1px solid #dfe5ee;border-radius:10px;background:#f7f9fc;color:#4d6484;font:inherit;font-size:12px;font-weight:600;cursor:pointer}
.ann-copy-button:disabled{color:#98a1af;background:#f5f6f8;cursor:default}
.ann-records-table td{font-size:12px!important;color:#475569;padding:18px 16px!important}
.ann-records-table td strong{display:block;font-weight:600;color:#344159;line-height:1.5}
.ann-cell-sub{display:block;color:#8591a2;font-size:11px;line-height:1.6;margin-top:4px}
.ann-records-table .ann-amount{font-size:15px!important;font-weight:650;white-space:nowrap;text-align:right;color:#344159}
.ann-status{display:inline-block;margin-top:8px;padding:4px 8px;border-radius:7px;font-size:10px;font-weight:600;line-height:1.4}
.ann-status.used{background:#edf3fb;color:#587da9}.ann-status.active{background:#edf7f1;color:#4c896b}.ann-status.elevated{background:#f1eef8;color:#8472a3}.ann-status.expired{background:#f2f4f7;color:#8c96a5}
.ann-page .ann-record-main{border-bottom:0}
.ann-page .ann-reason-row td{padding:0 16px 18px!important}
.ann-reason-row td>div{padding:12px 14px;background:#f8f9fc;border-left:3px solid #d8b2b8;border-radius:0 10px 10px 0}
.ann-reason-row span{font-size:10px;font-weight:600;color:#8992a3}.ann-reason-row p{margin:5px 0 0;font-size:12px;color:#596779;line-height:1.6;overflow-wrap:anywhere}
.ann-empty{display:flex;align-items:center;justify-content:center;min-height:170px;height:100%;font-size:13px;color:#8994a5}
.ann-page .ann-token-code{font-size:14px;letter-spacing:1px}


.ann-main-table .ann-record-main{border-bottom:1px solid #e9edf4!important}
.ann-main-table .ann-record-main:nth-child(even){background:#fafbfe}
.ann-main-table .ann-record-main:hover{background:#f0f5fc}
.ann-main-table .ann-record-main td{vertical-align:middle;padding:16px!important}
.ann-document-type{display:inline-flex;padding:4px 8px;border-radius:7px;font-size:10px;font-weight:650;letter-spacing:.02em;margin-bottom:7px}
.ann-document-type.receipt{background:#eaf2ff;color:#397dc6}.ann-document-type.invoice{background:#fff2e3;color:#bb791c}.ann-document-type.other{background:#f0eafa;color:#8662b6}
.ann-main-table .ann-document-number{font-size:13px;color:#344e73}
.ann-main-table .ann-reason-cell{width:30%;min-width:200px;max-width:360px;line-height:1.65;white-space:normal;overflow-wrap:anywhere}
.ann-reason-cell>span{color:#687589}
.ann-main-table .ann-amount>span{display:inline-block;color:#bb5365;background:#fdf0f3;padding:7px 10px;border-radius:9px;font-size:14px}
.ann-main-table .ann-token-reference{color:#b57c31}
.ann-main-table .ann-status.used{background:#e7f5ee;color:#34845f}

@keyframes fadeInChart {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </>
  );
};

export default Anulaciones;
